import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { EditableImage } from "@/components/EditableImage";
import { SafeImage } from "@/components/SafeImage";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { useState, useEffect } from "react";
import { DifficultyBadge } from "@/components/DifficultyBadge";

interface ExcursionCardProps {
  id: string;
  title: string;
  category: string;
  tags?: {
    coup_de_coeur?: boolean;
    impact_solidaire?: boolean;
  };
  resume?: string;
  debut?: string;
  duree?: string;
  prix_eur: number;
  participants_min: number;
  participants_max: number;
  niveau: number;
  cover_image?: string;
  original_image?: string;
  image?: string;
  whatsapp_catalog_id?: string;
}

export const ExcursionCard = ({ 
  id, title, category, tags, resume, debut, duree, 
  prix_eur, participants_min, participants_max, niveau, 
  cover_image, original_image, image, whatsapp_catalog_id
}: ExcursionCardProps) => {
  const { isAdmin } = useAdmin();
  const [currentImage, setCurrentImage] = useState(cover_image || image);
  const [displayImage, setDisplayImage] = useState(currentImage);

  useEffect(() => {
    const newImage = cover_image || image;
    if (newImage !== currentImage) {
      setCurrentImage(newImage);
      setDisplayImage(newImage);
    }
  }, [cover_image, image]);

  return (
    <Link 
      to={`/excursions/${id}`} 
      className="block focus-visible-ring rounded-lg"
      aria-label={`Voir les détails de l'excursion ${title}`}
    >
      <Card className="group hover:scale-[1.02] shadow-card transition-all duration-300 cursor-pointer overflow-hidden" role="article">
        {/* Image section */}
        <div className="relative aspect-[16/10]">
          {/* Image with overflow hidden */}
          <div className="absolute inset-0 overflow-hidden">
            {isAdmin ? (
              <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
                <EditableImage
                  src={displayImage || ""}
                  alt={title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  bucket="site-assets"
                  path={`excursions/${id}/card-cover.jpg`}
                  originalPath={`excursions/${id}/original.jpg`}
                  originalSrc={original_image || undefined}
                  aspectRatio={16/9}
                  onImageUpdate={async (newUrl) => {
                    const { error } = await supabase
                      .from('excursions')
                      .update({ cover_image: newUrl })
                      .eq('id', id);
                    if (!error) { setCurrentImage(newUrl); setDisplayImage(newUrl); }
                  }}
                  onOriginalSaved={async (originalUrl) => {
                    await supabase
                      .from('excursions')
                      .update({ original_image: originalUrl } as any)
                      .eq('id', id);
                  }}
                  onImageDelete={async () => {
                    const { error } = await supabase
                      .from('excursions')
                      .update({ cover_image: null })
                      .eq('id', id);
                    if (!error) { setCurrentImage(''); setDisplayImage(''); }
                  }}
                />
              </div>
            ) : (
              <SafeImage
                src={displayImage || "/placeholder.svg"}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          </div>

          {/* Badges — on top, inside relative parent */}
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <Badge variant="secondary" className="bg-card/95 text-foreground capitalize text-xs sm:text-sm px-3 py-1 backdrop-blur-sm shadow-md border-0">
              {category}
            </Badge>
          </div>

          <div className="absolute top-3 right-3 z-10 flex flex-col gap-2 pointer-events-none">
            {tags?.coup_de_coeur && (
              <Badge className="bg-primary text-primary-foreground font-semibold text-xs sm:text-sm px-2.5 py-1 shadow-md whitespace-nowrap border-0">
                ⭐ Coup de cœur
              </Badge>
            )}
            {tags?.impact_solidaire && (
              <Badge className="bg-green-600 text-white font-semibold text-xs sm:text-sm px-2.5 py-1 shadow-md whitespace-nowrap border-0">
                👫 Impact solidaire
              </Badge>
            )}
          </div>

          <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
            <div className="bg-card/95 backdrop-blur-sm text-primary px-3 py-1.5 rounded-lg shadow-md">
              <div className="text-lg sm:text-xl font-bold">{prix_eur}€</div>
            </div>
          </div>
        </div>
      
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-end mb-3">
            <DifficultyBadge niveau={niveau} size="sm" />
          </div>

          <h3 className="text-lg sm:text-xl font-semibold mb-1 text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </h3>

          <p className="text-sm font-bold text-primary mb-3">
            À partir de {prix_eur}€/pers.
          </p>
          
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {resume || 'Découvrez cette excursion unique à Rio de Janeiro.'}
          </p>
          
          <div className="grid grid-cols-2 gap-3 mb-5 text-xs sm:text-sm">
            <div className="flex items-center text-muted-foreground">
              <Clock className="h-4 w-4 mr-2 text-roca-blue-dark flex-shrink-0" />
              <span className="truncate">{duree || 'Variable'}</span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <Users className="h-4 w-4 mr-2 text-roca-red flex-shrink-0" />
              <span className="truncate">
                Jusqu'à {title.toLowerCase().includes('pain de sucre') ? '3' : '7'} pers.
              </span>
            </div>
          </div>

          <span className="block text-center text-sm text-muted-foreground group-hover:text-foreground transition-colors">
            Voir les détails →
          </span>
        </CardContent>
      </Card>
    </Link>
  );
};
